import React, { useState } from 'react';
import { LockIcon, ChevronUpIcon, ChatIcon } from './Icons';
import { useGallery, useBuildLog, useFeatures } from '../hooks/useCommunity';

type CommunityFilter = 'All' | 'MockGen' | 'StyleFinder' | 'Reframe';

const STATUS_MAP: Record<string, string> = {
  release: 'released',
  dev: 'in-dev',
  planned: 'planned',
};

export const CommunitySection: React.FC = () => {
  const [communityFilter, setCommunityFilter] = useState<CommunityFilter>('All');

  const appParam = communityFilter === 'All' ? 'all' : communityFilter.toLowerCase();
  const { items: galleryItems, toggleVote: toggleGalleryVote } = useGallery(appParam);
  const { entries: buildEntries } = useBuildLog();
  const { features, toggleVote: toggleFeatureVote } = useFeatures();

  const communityFilters: CommunityFilter[] = ['All', 'MockGen', 'StyleFinder', 'Reframe'];

  return (
    <section id="community" className="fs-page" style={{ paddingTop: 64, paddingBottom: 96 }}>
      <div style={{ marginBottom: 40 }}>
        <p className="fs-section-label" style={{ marginBottom: 8 }}>Community</p>
        <h2 style={{
          fontFamily: 'var(--fs-font-headline)',
          fontWeight: 700,
          fontSize: 'clamp(26px, 3vw, 32px)',
          color: 'var(--fs-text)',
          letterSpacing: '-0.02em',
        }}>See what designers are creating</h2>
      </div>

      {/* Visitor gallery placeholder */}
      <div className="visitor-only">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{
              background: 'var(--fs-surface)',
              border: '1px solid var(--fs-border)',
              borderRadius: 10,
              aspectRatio: '4/3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{ width: i * 12 + 20, height: i * 8 + 10, borderRadius: 4, background: 'var(--fs-surface-hi)', opacity: 0.3 }} />
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: 8, background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)' }}>
                <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 10, color: 'var(--fs-text-muted)' }}>@designer</p>
              </div>
            </div>
          ))}
        </div>

        {/* Locked CTA */}
        <div style={{
          textAlign: 'center',
          border: '1px solid var(--fs-border)',
          borderRadius: 'var(--fs-radius-card)',
          padding: '40px 24px',
          background: 'var(--fs-surface)',
        }}>
          <LockIcon size={24} className="" />
          <p style={{ fontFamily: 'var(--fs-font-headline)', fontWeight: 700, fontSize: 15, color: 'var(--fs-text)', marginTop: 12, marginBottom: 4 }}>
            Sign in to submit and vote
          </p>
          <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 13, color: 'var(--fs-text-muted)', marginBottom: 20 }}>
            Share your work, vote on features, follow the build log.
          </p>
          <a href="#" className="fs-btn-primary" style={{ fontSize: 12, padding: '10px 20px' }}>Join the Beta</a>
        </div>
      </div>

      {/* Logged-in interactive community */}
      <div className="logged-only">
        {/* Filter + submit */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 8, overflow: 'auto', paddingBottom: 4 }}>
            {communityFilters.map(f => (
              <button
                key={f}
                className={`fs-chip${communityFilter === f ? ' fs-chip--active' : ''}`}
                onClick={() => setCommunityFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
          <button className="fs-btn-primary" style={{ fontSize: 12, padding: '8px 20px', flexShrink: 0 }}>
            + Submit your work
          </button>
        </div>

        {/* Gallery interactive */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 48 }}>
          {galleryItems.map(item => (
            <div key={item.id} className="fs-gallery-item">
              <div style={{ width: '100%', height: '100%', background: 'var(--fs-surface-hi)' }}>
                {item.thumbnailUrl && (
                  <img src={item.thumbnailUrl} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
              </div>
              <div className="fs-gallery-vote-bar">
                <span style={{ fontFamily: 'var(--fs-font-body)', fontSize: 10, color: 'var(--fs-text-muted)' }}>{item.author.displayName}</span>
                <button
                  className={`fs-vote-btn${item.hasVoted ? ' voted' : ''}`}
                  onClick={() => toggleGalleryVote(item.id)}
                >
                  <ChevronUpIcon />
                  <span>{item.voteCount}</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Build Log */}
        <div style={{ marginBottom: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <p className="fs-section-label" style={{ marginBottom: 4 }}>Build Log</p>
              <h3 style={{ fontFamily: 'var(--fs-font-headline)', fontWeight: 700, fontSize: 20, color: 'var(--fs-text)', letterSpacing: '-0.02em' }}>
                We build in public
              </h3>
            </div>
            <button className="fs-btn-text" style={{ fontSize: 12 }}>All updates →</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {buildEntries.map(entry => {
              const dotStatus = STATUS_MAP[entry.type] || entry.type;
              const label = entry.type === 'release' ? 'Released'
                : entry.type === 'dev' ? 'In Dev'
                : 'Planned';
              const badgeClass = entry.type === 'release' ? 'beta'
                : entry.type === 'planned' ? 'alpha'
                : 'beta';
              const dateStr = new Date(entry.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

              return (
                <div key={entry.id} className="fs-build-entry">
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div className={`fs-build-dot fs-build-dot--${dotStatus}`} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 6 }}>
                        <p style={{ fontFamily: 'var(--fs-font-headline)', fontWeight: 700, fontSize: 13, color: 'var(--fs-text)' }}>{entry.title}</p>
                        <span style={{ fontFamily: 'var(--fs-font-body)', fontSize: 11, color: 'var(--fs-text-muted)', flexShrink: 0 }}>{dateStr}</span>
                      </div>
                      <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 12, color: 'var(--fs-text-muted)', lineHeight: 1.6 }}>{entry.description}</p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
                        <span className={`fs-badge fs-badge--${badgeClass}`} style={{ fontSize: 9 }}>
                          {label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Feature Voting */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div>
              <p className="fs-section-label" style={{ marginBottom: 4 }}>Vote next feature</p>
              <h3 style={{ fontFamily: 'var(--fs-font-headline)', fontWeight: 700, fontSize: 20, color: 'var(--fs-text)', letterSpacing: '-0.02em' }}>
                What gets built next
              </h3>
            </div>
            <button className="fs-btn-ghost" style={{ fontSize: 12, padding: '8px 16px' }}>Suggest a feature</button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {features.map(feature => (
              <div key={feature.id} className="fs-feature-row">
                <button
                  className={`fs-upvote-btn${feature.hasVoted ? ' voted' : ''}`}
                  onClick={() => toggleFeatureVote(feature.id)}
                  title="Upvote"
                >
                  <ChevronUpIcon />
                  <span>{feature.voteCount}</span>
                </button>
                <div style={{ flex: 1 }}>
                  <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 13, fontWeight: 600, color: 'var(--fs-text)' }}>{feature.title}</p>
                  <p style={{ fontFamily: 'var(--fs-font-body)', fontSize: 11, color: 'var(--fs-text-muted)', marginTop: 2 }}>{feature.description}</p>
                </div>
                <span style={{
                  fontFamily: 'var(--fs-font-body)',
                  fontSize: 10,
                  fontWeight: 700,
                  flexShrink: 0,
                  ...(feature.status === 'planned' || feature.status === 'in_progress' ? {
                    color: 'var(--fs-cyan)',
                    background: 'var(--fs-cyan-dim)',
                    border: '1px solid var(--fs-beta-border)',
                    padding: '4px 8px',
                    borderRadius: 4,
                  } : {
                    color: 'var(--fs-text-muted)',
                  }),
                }}>{feature.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
